<template>
  <Teleport to="body">
    <!-- Backdrop -->
    <Transition name="fade">
      <div v-if="open" class="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]" @click="$emit('close')"></div>
    </Transition>

    <!-- Panel -->
    <Transition name="slide-over">
      <div v-if="open" class="fixed inset-y-0 right-0 z-50 w-full sm:w-[80%] bg-white dark:bg-[#1C2128] shadow-2xl flex flex-col" @click.stop="handlePanelClick">
        <div class="flex-shrink-0 flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <div class="min-w-0">
            <h2 class="text-base font-semibold text-gray-900 dark:text-white truncate">{{ title }}</h2>
            <!-- Once there's real provenance to show (editing an already-saved
                 component), it replaces the generic instructional subtitle —
                 same slot, so the header height never shifts between modes.
                 Two icon-led fragments joined by a middot rather than a
                 sentence ("Added by X on Y") — reads as metadata, not prose,
                 the same convention as issue-tracker headers (Linear, GitHub,
                 Jira: "who · when"). -->
            <p v-if="locked && formattedCreatedAt" class="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500 truncate">
              <span v-if="addedByName" class="inline-flex items-center gap-1 min-w-0">
                <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"/>
                </svg>
                <span class="truncate">{{ addedByName }}</span>
              </span>
              <span v-if="addedByName" class="flex-shrink-0 text-gray-300 dark:text-gray-600">&middot;</span>
              <span class="inline-flex items-center gap-1 flex-shrink-0">
                <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" stroke-width="1.75"/>
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75V12l3.5 2"/>
                </svg>
                {{ formattedCreatedAt }}
              </span>
            </p>
            <p v-else class="text-xs text-gray-400 dark:text-gray-500 truncate">Pick a component, then categorize it for your org</p>
          </div>

          <div class="flex items-center gap-2 flex-shrink-0">
            <button type="button" @click="$emit('close')"
              class="px-4 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
              Cancel
            </button>
            <button type="button" @click="submit" :disabled="saving || !name.trim() || !type"
              class="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors">
              <svg v-if="saving" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              {{ saving ? 'Saving...' : submitLabel }}
            </button>
          </div>
        </div>

        <!-- Two panes at sm+: form on the left at a fixed, readable width; the
             right pane is a live preview today and is where versions/factsheet
             stats etc. will land as this panel grows. Collapses to a single
             column (form only) below sm — 80% of a phone screen is still too
             narrow to split. -->
        <div class="flex-1 min-h-0 flex flex-col sm:flex-row">
          <div class="w-full sm:w-[420px] sm:flex-shrink-0 min-h-0 flex flex-col sm:border-r sm:border-gray-100 dark:sm:border-gray-700">
            <div class="flex-1 overflow-y-auto px-5 sm:px-6 py-6 space-y-6">
            <!-- IT component -->
            <div class="relative name-suggestions">
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">IT Component<span v-if="!locked" class="text-red-500">*</span></label>

              <!-- Locked once this component has actually been saved — which
                   catalog entry it references can't be changed after the
                   fact (only type/vendor/description remain editable). -->
              <div v-if="locked" class="flex items-center gap-2 px-3.5 py-2.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-[#161b22]">
                <ItComponentLogo :name="name" :logo-url="matchedComponent?.logo_url" size="w-6 h-6" initial-text-size="text-[10px]" class="flex-shrink-0" />
                <span class="text-sm text-gray-700 dark:text-gray-300 truncate">{{ name }}</span>
              </div>

              <div v-else class="relative">
                <div :class="[error ? 'border-red-400 dark:border-red-500' : 'border-gray-200 dark:border-gray-700',
                             showSuggestions && suggestions.length ? 'rounded-t-lg border-b-transparent bg-gray-50 dark:bg-white/[0.04]' : 'rounded-lg bg-white dark:bg-[#0d1117]']"
                  class="w-full h-[42px] flex items-center gap-2 pl-3.5 pr-3 border">
                  <ItComponentLogo v-if="matchedComponent" :name="matchedComponent.name" :logo-url="matchedComponent.logo_url"
                    size="w-5 h-5" initial-text-size="text-[9px]" class="flex-shrink-0" />
                  <input ref="nameInput" v-model="name" type="text" placeholder="Search or type a component name, e.g. PostgreSQL" autocomplete="off"
                    @keydown="handleNameKeydown" @focus="openNameSuggestions" @input="openNameSuggestions"
                    class="flex-1 min-w-0 text-sm bg-transparent border-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 placeholder:text-xs focus:outline-none focus:ring-0" />
                  <svg class="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform"
                    :class="showSuggestions ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
                  </svg>
                </div>

                <!-- Positioned relative to this input row specifically (top-full),
                     not the outer field group — keeping it out from under the
                     hint/error text below avoids the dropdown inheriting that
                     text's static-flow offset and floating a visible gap below
                     the input. Fused to the field above it (no gap, no top
                     border, rounded only at the bottom) so it reads as one
                     combined combobox shape rather than two stacked boxes —
                     matches AttachDependencyForm's component/version pickers. -->
                <div v-if="showSuggestions && suggestions.length"
                  class="absolute z-30 top-full left-0 w-full bg-white dark:bg-[#1C2128] border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-lg shadow-lg py-1.5 max-h-56 overflow-y-auto">
                  <button v-for="(suggestion, index) in suggestions" :key="suggestion.name" type="button"
                    :ref="(el) => setSuggestionRef(el, index)"
                    @click="selectSuggestion(suggestion)" @mouseenter="activeSuggestionIndex = index"
                    :class="index === activeSuggestionIndex ? 'bg-gray-50 dark:bg-gray-800' : ''"
                    class="w-full flex items-center gap-2.5 text-left px-3.5 py-2 transition-colors">
                    <ItComponentLogo :name="suggestion.name" :logo-url="suggestion.logo_url" size="w-7 h-7" initial-text-size="text-[10px]" />
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                        <span class="truncate">{{ suggestion.name }}</span>
                        <!-- Already in this org's stack — still selectable
                             (an org can add the same catalog entry more than
                             once, e.g. distinct type/vendor overrides), just
                             flagged so picking it again is a deliberate choice. -->
                        <span v-if="isAlreadyAdded(suggestion)" class="inline-flex flex-shrink-0 items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
                          Already added
                        </span>
                      </p>
                      <p class="text-xs text-gray-400 dark:text-gray-500 truncate">{{ typeLabel(suggestion.type) }}<span v-if="suggestion.vendor"> · {{ suggestion.vendor }}</span></p>
                    </div>
                  </button>
                </div>
              </div>
              <p v-if="error" class="mt-1.5 text-xs text-red-500 dark:text-red-400 font-medium">{{ error }}</p>
              <p v-else-if="locked" class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">The component itself can't be changed once added.</p>
              <p v-else class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">Search the shared catalog, or type a new name.</p>
            </div>

            <!-- Type -->
            <div class="relative type-suggestions">
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Type<span class="text-red-500">*</span></label>
              <div class="relative">
                <div :class="showTypeSuggestions ? 'rounded-t-lg border-b-transparent bg-gray-50 dark:bg-white/[0.04] border-gray-200 dark:border-gray-700' : 'rounded-lg bg-white dark:bg-[#0d1117] border-gray-200 dark:border-gray-700'"
                  class="w-full h-[42px] flex items-center gap-2 pl-3.5 pr-3 border">
                  <input ref="typeInput" v-model="typeQuery" type="text" placeholder="Search a type" autocomplete="off"
                    @keydown="handleTypeKeydown" @focus="handleTypeFocus" @input="openTypeSuggestions"
                    class="flex-1 min-w-0 text-sm bg-transparent border-0 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-0" />
                  <svg class="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform"
                    :class="showTypeSuggestions ? 'rotate-180' : ''" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
                  </svg>
                </div>

                <div v-if="showTypeSuggestions"
                  class="absolute z-30 top-full left-0 w-full bg-white dark:bg-[#1C2128] border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-lg shadow-lg py-1.5 max-h-64 overflow-y-auto">
                  <button v-for="(option, index) in filteredTypeOptions" :key="option.value" type="button"
                    :ref="(el) => setTypeSuggestionRef(el, index)"
                    @click="selectType(option)" @mouseenter="activeTypeIndex = index"
                    :class="index === activeTypeIndex ? 'bg-gray-50 dark:bg-gray-800' : ''"
                    class="w-full flex items-center gap-2.5 text-left px-3.5 py-2 transition-colors">
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex items-center gap-1.5">
                        {{ option.label }}
                        <svg v-if="option.value === type" class="w-3.5 h-3.5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                        </svg>
                      </p>
                      <p class="text-xs text-gray-400 dark:text-gray-500 truncate">{{ option.description }}</p>
                    </div>
                  </button>
                  <p v-if="filteredTypeOptions.length === 0" class="px-3.5 py-4 text-xs text-gray-400 dark:text-gray-500 text-center">No matching type.</p>
                </div>
              </div>
              <p class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">Categorizes what kind of component this is.</p>
            </div>

            <!-- Vendor -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Vendor <span class="text-gray-400 dark:text-gray-500 font-normal normal-case">(optional)</span></label>
              <input v-model="vendor" type="text" placeholder="e.g. PostgreSQL Global Development Group"
                @keydown.enter="submit"
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
              <p class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">Who builds or maintains this component.</p>
            </div>

            <!-- Description -->
            <div>
              <label class="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Description <span class="text-gray-400 dark:text-gray-500 font-normal normal-case">(optional)</span></label>
              <textarea v-model="description" rows="3" placeholder="What is this component, briefly?"
                class="w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow resize-none"></textarea>
            </div>
            </div>

            <!-- Danger zone: only once this component has actually been
                 saved — nothing to remove before that. Pinned to the bottom
                 of the left pane (outside the scroll area above) rather than
                 flowing after Description, so it's always reachable without
                 scrolling and reads as a deliberate, separate action. -->
            <div v-if="orgComponentId" class="flex-shrink-0 px-5 sm:px-6 py-3 border-t border-gray-100 dark:border-gray-700">
              <button type="button" @click="showDeleteConfirm = true"
                class="w-full inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                </svg>
                Remove component
              </button>
            </div>
          </div>

          <!-- Right pane: live preview now, reserved for versions/factsheet
               stats etc. once that scope is built. Hidden below sm — the
               form alone already fills a full-width mobile drawer. -->
          <div class="hidden sm:flex flex-1 flex-col overflow-y-auto bg-gray-50 dark:bg-[#0d1117] px-6 py-6">
            <p class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-3">Preview</p>

            <div v-if="name.trim()" class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 p-5">
              <div class="flex items-start gap-3">
                <ItComponentLogo :name="name.trim()" :logo-url="matchedComponent?.logo_url" size="w-11 h-11" initial-text-size="text-sm" />
                <div class="min-w-0 flex-1 pt-0.5">
                  <div class="flex items-center gap-2">
                    <h3 class="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{{ name.trim() }}</h3>
                    <span v-if="type" class="inline-flex flex-shrink-0 items-center px-2 py-0.5 rounded-full text-[11px] font-medium" :class="typeBadgeClass(type)">
                      {{ typeLabel(type) }}
                    </span>
                    <span v-else class="inline-flex flex-shrink-0 items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500">
                      No type
                    </span>
                  </div>
                  <p class="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{{ vendor.trim() || 'No vendor listed' }}</p>
                </div>
              </div>
            </div>

            <div v-else class="flex-1 flex flex-col items-center justify-center text-center py-12">
              <div class="w-12 h-12 rounded-xl bg-white dark:bg-[#1C2128] border border-gray-100 dark:border-gray-700 flex items-center justify-center mb-3">
                <svg class="w-5 h-5 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 17.25v-.228a4.5 4.5 0 0 0-.12-1.03l-2.268-9.64a3.375 3.375 0 0 0-3.285-2.602H7.923a3.375 3.375 0 0 0-3.285 2.602l-2.268 9.64a4.5 4.5 0 0 0-.12 1.03v.228m19.5 0a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3m19.5 0a3 3 0 0 0-3-3H5.25a3 3 0 0 0-3 3m16.5 0h.008v.008h-.008v-.008Zm-3 0h.008v.008h-.008v-.008Z"/>
                </svg>
              </div>
              <p class="text-sm font-medium text-gray-400 dark:text-gray-500">Nothing selected yet</p>
              <p class="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-[220px]">Search for a component on the left to see a preview here.</p>
            </div>

            <!-- Versions: shown as soon as a name is picked, even before the
                 first Save — OrgComponentVersionsSection renders its own
                 disabled preview until orgComponentId is real. -->
            <div v-if="name.trim()" class="mt-4">
              <OrgComponentVersionsSection :organization-id="organizationId" :org-component-id="orgComponentId"
                :global-component-id="globalComponentId" :highlight="highlightVersions"
                :component-name="name.trim()" :component-logo-url="matchedComponent?.logo_url || ''"
                @notify="(message, type) => emit('notify', message, type)" />
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Delete confirmation — Teleported to body (like the panel itself) so
         it renders above everything, including the slide-over's own z-50. -->
    <Transition name="modal" appear>
      <div v-if="showDeleteConfirm" class="fixed inset-0 z-[60] flex items-center justify-center p-4" @click.self="showDeleteConfirm = false">
        <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
        <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-w-sm w-full p-6 animate-in">
          <div class="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
            </svg>
          </div>
          <h3 class="text-base font-semibold text-gray-900 dark:text-white text-center mb-1.5">Remove "{{ name }}"?</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">This action cannot be undone.</p>
          <div class="flex gap-3">
            <button @click="showDeleteConfirm = false" type="button"
              class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
              Cancel
            </button>
            <button @click="confirmDelete" type="button"
              class="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors">
              Remove
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { TYPE_OPTIONS, typeLabel, typeBadgeClass } from 'controllers/vue_components/organizations/it_components/itComponentOptions.js'
import { useGlobalComponentsStore } from 'stores/globalComponents.js'
import { useOrgComponentOptionsStore } from 'stores/orgComponentOptions.js'
import ItComponentLogo from 'controllers/vue_components/organizations/it_components/ItComponentLogo.vue'
import OrgComponentVersionsSection from 'controllers/vue_components/organizations/it_components/OrgComponentVersionsSection.vue'

const globalComponentsStore = useGlobalComponentsStore()
// Read only here to flag catalog suggestions the org already has — fetched
// (cached, TTL-shared) independently of OrgComponentsPanel's own use of it,
// since this slide-over can't assume that parent has fetched it yet.
const componentOptionsStore = useOrgComponentOptionsStore()

const props = defineProps({
  // The component stays mounted between opens (like BookingSlideOver) so the
  // backdrop fade and panel slide can both animate in AND out — a plain
  // parent v-if would destroy this component immediately on close, before
  // any leave transition gets a chance to run.
  open: { type: Boolean, default: false },
  organizationId: { type: String, required: true },
  title: { type: String, required: true },
  submitLabel: { type: String, default: 'Add' },
  saving: { type: Boolean, default: false },
  initialName: { type: String, default: '' },
  initialType: { type: String, default: '' },
  initialVendor: { type: String, default: '' },
  initialDescription: { type: String, default: '' },
  // Only set when editing an already-saved component — gates whether the
  // Versions section can render at all (nothing to attach a version to
  // before the first Save).
  orgComponentId: { type: String, default: '' },
  globalComponentId: { type: String, default: '' },
  // True for the brief window right after this component was just created —
  // draws attention to the Versions section that only just became usable.
  highlightVersions: { type: Boolean, default: false },
  error: { type: String, default: '' },
  // Only set when editing an already-saved component (see OrgComponentsPanel's
  // modal.editing) — surfaced as a small "Added ... by ..." note under the
  // now-locked IT Component field, same provenance info OrgComponentVersionForm
  // already shows per version via added_by_name.
  createdAt: { type: String, default: '' },
  addedByName: { type: String, default: '' },
})

// Once a component has been saved, which catalog entry it references can't
// be changed from here anymore — only type/vendor/description stay editable.
const locked = computed(() => !!props.orgComponentId)

// Matches OrgComponentVersionsSection's own date formatting so "Added ..."
// dates read consistently across the IT Components page.
const formattedCreatedAt = computed(() => {
  if (!props.createdAt) return ''
  return new Date(props.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
})

const emit = defineEmits(['close', 'submit', 'notify', 'delete'])

const showDeleteConfirm = ref(false)

function confirmDelete() {
  showDeleteConfirm.value = false
  emit('delete', props.orgComponentId)
}

const name = ref(props.initialName)
const type = ref(props.initialType)
const vendor = ref(props.initialVendor)
const description = ref(props.initialDescription)
const nameInput = ref(null)
const showSuggestions = ref(false)
const activeSuggestionIndex = ref(-1)
const suggestionRefs = ref([])

// Reset/populate the form every time the panel opens — the component itself
// persists across opens, only its props (which record is being edited, if
// any) change, so this can't rely on a fresh mount the way the old modal did.
watch(() => props.open, (isOpen) => {
  showDeleteConfirm.value = false
  if (!isOpen) return
  name.value = props.initialName
  type.value = props.initialType
  vendor.value = props.initialVendor
  description.value = props.initialDescription
  typeQuery.value = props.initialType ? typeLabel(props.initialType) : ''
  showTypeSuggestions.value = false
  // Only a truly blank Add (the "+ Add Component" button) auto-opens the
  // catalog dropdown and focuses the field. Editing an existing component,
  // or opening prefilled from a dragged/clicked catalog entry, already has
  // a name — nothing should grab focus or pop a dropdown open in either
  // case, so the user can land on the panel and click straight into
  // whichever field (or Save) they actually want.
  const isBlankAdd = !props.initialName
  showSuggestions.value = isBlankAdd
  if (isBlankAdd) nextTick(() => nameInput.value?.focus())
})

// Freeform typing is always allowed. This is only a helper: if what the user
// typed matches a catalog entry, picking it also prefills type and vendor.
// An empty query still shows a starting slice of the catalog (mirrors the
// Type field's dropdown) rather than nothing, so the field reads as a real
// dropdown you can browse, not just an autocomplete that needs typing first.
const suggestions = computed(() => {
  const query = name.value.trim().toLowerCase()
  if (query.length === 0) return globalComponentsStore.components.slice(0, 8)
  if (query.length < 2) return []
  return globalComponentsStore.components
    .filter((component) => component.name.toLowerCase().includes(query))
    .slice(0, 8)
})

// The org already has a component referencing this catalog entry — still
// pickable (see the template's own comment), just flagged. Only relevant to
// suggestions here, since this field is locked (no dropdown at all) once
// editing an existing component — see `locked` above.
const existingGlobalComponentIds = computed(() =>
  new Set(componentOptionsStore.components.map((c) => c.global_component_id)))

function isAlreadyAdded(suggestion) {
  return existingGlobalComponentIds.value.has(suggestion.id)
}

// Drives the logo shown inside the Name field itself — recomputed off the
// name field directly (not a separate "selected" flag) so it also lights up
// when editing an existing component whose name already matches the catalog,
// not just right after picking a suggestion.
const matchedComponent = computed(() => {
  const query = name.value.trim().toLowerCase()
  if (!query) return null
  return globalComponentsStore.components.find((component) => component.name.toLowerCase() === query) || null
})

watch(suggestions, () => {
  activeSuggestionIndex.value = -1
  suggestionRefs.value = []
})

function setSuggestionRef(el, index) {
  if (el) suggestionRefs.value[index] = el
}

watch(activeSuggestionIndex, (index) => {
  if (index >= 0) suggestionRefs.value[index]?.scrollIntoView({ block: 'nearest' })
})

function selectSuggestion(suggestion) {
  name.value = suggestion.name
  type.value = suggestion.type
  typeQuery.value = suggestion.type ? typeLabel(suggestion.type) : ''
  if (!vendor.value.trim()) vendor.value = suggestion.vendor || ''
  if (!description.value.trim()) description.value = suggestion.description || ''
  showSuggestions.value = false
  activeSuggestionIndex.value = -1
}

// Only one dropdown open at a time — opening this one closes the Type
// dropdown first (snapping its text back to the last confirmed selection)
// rather than letting both stack up.
function openNameSuggestions() {
  if (showTypeSuggestions.value) {
    showTypeSuggestions.value = false
    activeTypeIndex.value = -1
    snapTypeQueryBack()
  }
  showSuggestions.value = true
}

function handleNameKeydown(e) {
  const hasOpenSuggestions = showSuggestions.value && suggestions.value.length > 0

  if (hasOpenSuggestions && e.key === 'ArrowDown') {
    e.preventDefault()
    activeSuggestionIndex.value = (activeSuggestionIndex.value + 1) % suggestions.value.length
  } else if (hasOpenSuggestions && e.key === 'ArrowUp') {
    e.preventDefault()
    activeSuggestionIndex.value = activeSuggestionIndex.value <= 0
      ? suggestions.value.length - 1
      : activeSuggestionIndex.value - 1
  } else if (hasOpenSuggestions && e.key === 'Escape') {
    showSuggestions.value = false
    activeSuggestionIndex.value = -1
  } else if (e.key === 'Enter') {
    if (hasOpenSuggestions && activeSuggestionIndex.value >= 0) {
      e.preventDefault()
      selectSuggestion(suggestions.value[activeSuggestionIndex.value])
    } else {
      submit()
    }
  }
}

// ── Type — searchable dropdown (fixed enum, not freeform like Name) ─────────
// `type` holds the submitted enum value; `typeQuery` is the text shown/typed
// in the input. They're kept separate so a search that doesn't end in a pick
// can snap back to the last confirmed selection's label instead of leaving
// stray text that doesn't map to a real type.
const typeQuery = ref(props.initialType ? typeLabel(props.initialType) : '')
const typeInput = ref(null)
const showTypeSuggestions = ref(false)
const activeTypeIndex = ref(-1)
const typeSuggestionRefs = ref([])

const filteredTypeOptions = computed(() => {
  const query = typeQuery.value.trim().toLowerCase()
  if (!query) return TYPE_OPTIONS
  return TYPE_OPTIONS.filter((option) =>
    option.label.toLowerCase().includes(query) || option.description.toLowerCase().includes(query))
})

watch(filteredTypeOptions, () => {
  activeTypeIndex.value = -1
  typeSuggestionRefs.value = []
})

watch(activeTypeIndex, (index) => {
  if (index >= 0) typeSuggestionRefs.value[index]?.scrollIntoView({ block: 'nearest' })
})

function setTypeSuggestionRef(el, index) {
  if (el) typeSuggestionRefs.value[index] = el
}

function selectType(option) {
  type.value = option.value
  typeQuery.value = option.label
  showTypeSuggestions.value = false
  activeTypeIndex.value = -1
}

// Only one dropdown open at a time — opening this one closes the Name
// dropdown first rather than letting both stack up.
function openTypeSuggestions() {
  if (showSuggestions.value) {
    showSuggestions.value = false
    activeSuggestionIndex.value = -1
  }
  showTypeSuggestions.value = true
}

function handleTypeFocus() {
  openTypeSuggestions()
  nextTick(() => typeInput.value?.select())
}

function snapTypeQueryBack() {
  typeQuery.value = type.value ? typeLabel(type.value) : ''
}

function handleTypeKeydown(e) {
  const hasOpen = showTypeSuggestions.value

  if (hasOpen && e.key === 'ArrowDown') {
    e.preventDefault()
    if (filteredTypeOptions.value.length) activeTypeIndex.value = (activeTypeIndex.value + 1) % filteredTypeOptions.value.length
  } else if (hasOpen && e.key === 'ArrowUp') {
    e.preventDefault()
    if (filteredTypeOptions.value.length) {
      activeTypeIndex.value = activeTypeIndex.value <= 0 ? filteredTypeOptions.value.length - 1 : activeTypeIndex.value - 1
    }
  } else if (hasOpen && e.key === 'Escape') {
    showTypeSuggestions.value = false
    activeTypeIndex.value = -1
    snapTypeQueryBack()
  } else if (e.key === 'Enter') {
    e.preventDefault()
    if (hasOpen && activeTypeIndex.value >= 0) {
      selectType(filteredTypeOptions.value[activeTypeIndex.value])
    } else if (hasOpen && filteredTypeOptions.value.length === 1) {
      selectType(filteredTypeOptions.value[0])
    }
  }
}

// Bound directly on the panel's own root element (with .stop, see template)
// rather than on `document`. A document-level listener would also catch the
// very click that opens the panel — e.g. the "Add Component" button lives
// outside this Teleported panel, so that click bubbles to `document` before
// Vue has even rendered the panel/dropdown, immediately toggling it back
// shut. Scoping the listener to the panel itself sidesteps that race
// entirely: it only ever sees clicks that land inside the already-rendered
// panel. Clicking the backdrop closes the whole panel via its own handler,
// which covers every other click (the backdrop covers the full viewport
// while open), so nothing further is needed for true outside clicks.
function handlePanelClick(e) {
  if (!e.target.closest('.name-suggestions')) showSuggestions.value = false
  if (!e.target.closest('.type-suggestions') && showTypeSuggestions.value) {
    showTypeSuggestions.value = false
    activeTypeIndex.value = -1
    snapTypeQueryBack()
  }
}

function submit() {
  if (!name.value.trim() || !type.value) return

  emit('submit', {
    name: name.value.trim(),
    type: type.value,
    vendor: vendor.value.trim(),
    description: description.value.trim(),
  })
}

onMounted(() => {
  globalComponentsStore.fetchIfNeeded(props.organizationId)
  componentOptionsStore.fetchIfNeeded(props.organizationId)
})
</script>

<style scoped>
.slide-over-enter-active { transition: transform 0.25s ease; }
.slide-over-leave-active { transition: transform 0.20s ease; }
.slide-over-enter-from,
.slide-over-leave-to    { transform: translateX(100%); }

.fade-enter-active { transition: opacity 0.2s ease; }
.fade-leave-active { transition: opacity 0.15s ease; }
.fade-enter-from,
.fade-leave-to    { opacity: 0; }

.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-active .animate-in { transition: transform 0.2s ease; }
.modal-enter-from .animate-in { transform: scale(0.96) translateY(8px); }
</style>
